import React, { useState, useCallback, useRef } from 'react';
import { Box, Paper, Typography, CircularProgress } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';

/**
 * Drop zone component for DOCX file selection.
 * Supports both drag & drop and click-to-select.
 */
function DocxDropZone({ onFileSelect, selectedFile, isLoading, disabled }) {
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (disabled || isLoading) return;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.name.toLowerCase().endsWith('.docx')) {
        onFileSelect(file);
      } else {
        alert('Please select a .docx file');
      }
    }
  }, [onFileSelect, disabled, isLoading]);

  const handleChange = useCallback((e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      onFileSelect(files[0]);
    }
  }, [onFileSelect]);

  const handleClick = useCallback(() => {
    if (!disabled && !isLoading) {
      inputRef.current?.click();
    }
  }, [disabled, isLoading]);

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Paper
      sx={{
        p: 6,
        mb: 4,
        border: dragActive ? '3px dashed #1976d2' : '2px dashed #ccc',
        backgroundColor: dragActive ? 'rgba(25, 118, 210, 0.05)' : 'transparent',
        transition: 'all 0.3s ease',
        cursor: disabled || isLoading ? 'default' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        textAlign: 'center',
      }}
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
      onClick={handleClick}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        onChange={handleChange}
        style={{ display: 'none' }}
        disabled={disabled || isLoading}
      />

      {isLoading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress />
          <Typography variant="body1" color="text.secondary">
            Looking up metadata...
          </Typography>
        </Box>
      ) : selectedFile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
          <DescriptionIcon sx={{ fontSize: 48, color: 'primary.main' }} />
          <Typography variant="h6">{selectedFile.name}</Typography>
          <Typography variant="body2" color="text.secondary">
            {formatFileSize(selectedFile.size)}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Drop another file or click to replace
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CloudUploadIcon sx={{ fontSize: 48, color: dragActive ? 'primary.main' : 'text.secondary' }} />
          <Typography variant="h5" color={dragActive ? 'primary.main' : 'text.primary'}>
            {dragActive ? 'Drop DOCX here' : 'Drag & Drop DOCX'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            or click to select a file
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Maximum file size: 500 MB
          </Typography>
        </Box>
      )}
    </Paper>
  );
}

export default DocxDropZone;
